(() => {
  if (customElements.get('bbb-diy-clinic')) return;
  class BBBClinic extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.modules = [];
      this.dialog = this.querySelector('[data-dialog]');
      this.onPageShow = event => { if (event.persisted) location.reload(); };
      this.onPageHide = () => {
        this.listAbort?.abort();
        this.videoAbort?.abort();
        this.dialog.close();
        this.modules = [];
        this.querySelector('[data-grid]').replaceChildren();
      };
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('pagehide', this.onPageHide);
      this.querySelector('[data-search]').addEventListener('input', () => this.render());
      this.querySelector('[data-retry]').addEventListener('click', () => this.load());
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => {
        this.videoAbort?.abort();
        this.querySelector('[data-player]').replaceChildren();
        this.querySelector('[data-chapters]').replaceChildren();
        this.opener?.focus();
      });
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      });
      this.load();
    }
    disconnectedCallback() {
      this.listAbort?.abort();
      this.videoAbort?.abort();
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('pagehide', this.onPageHide);
    }
    element(tag, className, content) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (content !== undefined) element.textContent = content;
      return element;
    }
    readModules(root) {
      const modules = [];
      for (const item of root.querySelectorAll('template[data-clinic-module]')) {
        const module = JSON.parse(item.dataset.clinicModule);
        const id = String(module.vimeoId || '').trim();
        const hash = String(module.vimeoHash || '').trim();
        if (!/^[1-9]\d*$/.test(id) || (hash && !/^[a-zA-Z0-9]+$/.test(hash))) continue;
        const url = new URL(`https://player.vimeo.com/video/${id}`);
        url.searchParams.set('dnt', '1');
        url.searchParams.set('badge', '0');
        if (hash) url.searchParams.set('h', hash);
        module.playerUrl = url.toString();
        module.chapters = String(module.chapterLines || '').split(/\r?\n/).flatMap(line => {
          const match = line.trim().match(/^(\d+)\|(.+)$/);
          if (!match) return [];
          const seconds = Number(match[1]);
          return Number.isSafeInteger(seconds) && seconds >= 0 ? [{seconds, title: match[2].trim()}] : [];
        });
        modules.push(module);
      }
      return modules;
    }
    async request(handle, signal) {
      const url = new URL(location.pathname, location.origin);
      const current = new URL(location.href);
      for (const key of ['view', 'preview_theme_id']) {
        if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      }
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('_clinic', String(Date.now()));
      const response = await fetch(url, {credentials: 'same-origin', cache: 'no-store', signal});
      if (!response.ok) throw new Error('unavailable');
      if (new URL(response.url).origin !== location.origin) throw new Error('sign_in');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const root = document.querySelector('bbb-diy-clinic');
      if (!root) throw new Error('sign_in');
      const modules = this.readModules(root);
      if (!handle) return {modules};
      const module = modules.find(item => item.handle === handle);
      if (!module) throw new Error('unavailable');
      return {module};
    }
    async load() {
      this.listAbort?.abort();
      this.listAbort = new AbortController();
      this.modules = [];
      this.querySelector('[data-grid]').replaceChildren();
      this.querySelector('[data-filters]').hidden = true;
      this.querySelector('[data-retry]').hidden = true;
      this.querySelector('[data-sign-in-link]').hidden = true;
      this.querySelector('[data-status]').textContent = this.dataset.loading;
      try {
        const {modules} = await this.request(null, this.listAbort.signal);
        if (!Array.isArray(modules)) throw new Error('response');
        this.modules = modules;
        this.querySelector('[data-filters]').hidden = !modules.length;
        this.render();
        const requested = new URLSearchParams(location.hash.slice(1)).get('module');
        const module = modules.find(m => m.handle === requested);
        if (module) this.open(module, null);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.querySelector('[data-status]').textContent = error.message === 'sign_in' ? this.dataset.signIn : this.dataset.error;
        this.querySelector('[data-retry]').hidden = error.message === 'sign_in';
        this.querySelector('[data-sign-in-link]').hidden = error.message !== 'sign_in';
      }
    }
    render() {
      const query = this.querySelector('[data-search]').value.trim().toLocaleLowerCase();
      const visible = this.modules.filter(m => [m.title, m.code, m.description, m.equipment, m.topic].filter(Boolean).join(' ').toLocaleLowerCase().includes(query));
      const grid = this.querySelector('[data-grid]');
      grid.replaceChildren();
      this.querySelector('[data-status]').textContent = !this.modules.length ? this.dataset.empty : !visible.length ? this.dataset.noResults : '';
      const categories = new Map();
      for (const module of visible) {
        const categoryName = String(module.topic || '').trim().replace(/\s+/g, ' ') || this.dataset.otherCategory;
        const categoryKey = categoryName.toLocaleLowerCase();
        if (!categories.has(categoryKey)) {
          const group = this.element('section', 'bbb-clinic-category');
          const heading = this.element('h2', 'bbb-clinic-category-title', categoryName);
          heading.id = `ClinicCategory-${this.dataset.sectionId}-${categories.size}`;
          group.setAttribute('aria-labelledby', heading.id);
          const cards = this.element('div', 'bbb-clinic-grid');
          group.append(heading, cards);
          grid.append(group);
          categories.set(categoryKey, cards);
        }
        const card = this.element('article', 'bbb-clinic-card');
        const top = this.element('div', 'bbb-clinic-card-top');
        if (module.thumbnailUrl) {
          try {
            const url = new URL(module.thumbnailUrl);
            if (url.protocol === 'https:' && (url.hostname === 'i.vimeocdn.com' || url.hostname === 'cdn.shopify.com')) {
              const thumbnail = this.element('img', 'bbb-clinic-thumbnail');
              thumbnail.alt = '';
              thumbnail.width = 640;
              thumbnail.height = 360;
              thumbnail.loading = 'lazy';
              thumbnail.decoding = 'async';
              thumbnail.referrerPolicy = 'strict-origin-when-cross-origin';
              thumbnail.addEventListener('error', () => {
                thumbnail.remove();
                top.classList.remove('has-thumbnail');
              }, {once: true});
              thumbnail.src = url.href;
              top.classList.add('has-thumbnail');
              top.append(thumbnail);
            }
          } catch {}
        }
        top.append(this.element('span', 'bbb-clinic-code', module.code));
        if (module.duration) top.append(this.element('span', 'bbb-clinic-duration', module.duration));
        const play = this.element('span', 'bbb-clinic-play', '▶');
        play.setAttribute('aria-hidden', 'true');
        top.append(play);
        const body = this.element('div', 'bbb-clinic-card-body');
        const watch = this.element('button', '', this.dataset.watch);
        watch.type = 'button';
        watch.setAttribute('aria-label', `${this.dataset.watch}: ${module.title}`);
        watch.addEventListener('click', () => this.open(module, watch));
        body.append(this.element('h3', '', module.title), this.element('p', '', module.description), this.element('span', 'bbb-clinic-equipment', module.equipment), watch);
        card.append(top, body);
        categories.get(categoryKey).append(card);
      }
    }
    async open(module, opener) {
      this.opener = opener;
      this.querySelector('[data-title]').textContent = module.title;
      this.querySelector('[data-description]').textContent = module.description;
      this.querySelector('[data-chapters]').replaceChildren();
      this.dialog.showModal();
      await this.play(module.handle, 0, true);
    }
    async play(handle, seconds, buildChapters = false) {
      this.videoAbort?.abort();
      const controller = new AbortController();
      this.videoAbort = controller;
      const stage = this.querySelector('[data-player]');
      stage.replaceChildren();
      this.querySelector('[data-video-status]').textContent = this.dataset.loading;
      try {
        const {module} = await this.request(handle, controller.signal);
        if (controller.signal.aborted || !this.dialog.open) return;
        const url = new URL(module.playerUrl);
        if (url.origin !== 'https://player.vimeo.com' || !/^\/video\/[1-9]\d*$/.test(url.pathname)) throw new Error('player');
        url.hash = `t=${Math.floor(seconds / 60)}m${seconds % 60}s`;
        const frame = this.element('iframe');
        frame.title = module.title;
        frame.src = url.toString();
        frame.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
        frame.allowFullscreen = true;
        frame.referrerPolicy = 'strict-origin-when-cross-origin';
        stage.append(frame);
        this.querySelector('[data-video-status]').textContent = this.dataset.playerHelp;
        if (buildChapters) {
          const chapters = this.querySelector('[data-chapters]');
          for (const chapter of module.chapters) {
            const time = `${Math.floor(chapter.seconds / 60)}:${String(chapter.seconds % 60).padStart(2, '0')}`;
            const button = this.element('button', '', `${time} · ${chapter.title}`);
            button.type = 'button';
            button.dataset.seconds = String(chapter.seconds);
            button.addEventListener('click', () => this.play(handle, chapter.seconds));
            chapters.append(button);
          }
        }
        this.querySelectorAll('[data-seconds]').forEach(button => button.setAttribute('aria-current', Number(button.dataset.seconds) === seconds ? 'true' : 'false'));
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.querySelector('[data-video-status]').textContent = error.message === 'sign_in' ? this.dataset.signIn : this.dataset.unavailable;
      }
    }
  }
  customElements.define('bbb-diy-clinic', BBBClinic);
})();
